import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { createSolanaActionSource } from '@pkg/solana'
import { ApiSecretRequiredException } from '../../errors'
import type { FetchApiData } from '../../types'
import { networkEvmChainIds, solanaNetwork } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  config,
  from,
  createHeaders,
  proxy,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/wallet/deposits',
    query: {
      ...(from ? { from: Math.floor(from / 1000).toString() } : {}),
      ...(to ? { to: Math.ceil(to / 1000).toString() } : {}),
      limit: config.limit.toString(),
    },
    responseSchema,
  })

  // Sort by timestamp in ascending order (as per README)
  const sortedDeposits = response.sort(
    (a, b) => Number.parseInt(a.timestamp) - Number.parseInt(b.timestamp),
  )

  // Data processing
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedDeposits) {
    // Skip invalid or blocked deposits
    if (item.status === 'INVALID' || item.status === 'BLOCKED') {
      continue
    }

    // Stop processing if deposit is still pending
    if (
      item.status === 'REVIEW' ||
      item.status === 'PEND' ||
      item.status === 'TRACK'
    ) {
      stopped = true
      break
    }

    // Convert timestamp from seconds to milliseconds
    const timestamp = Number.parseInt(item.timestamp) * 1000

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    // Create EVM source if applicable
    if (item.chain && item.txid) {
      const evmChainId = networkEvmChainIds[item.chain]
      if (evmChainId && isHex(item.txid)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.txid,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (item.chain === solanaNetwork && item.txid) {
      sources.push(createSolanaActionSource({ signature: item.txid }))
    }
  }

  return {
    actions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
